import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    useToast,
} from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlatformEnum } from '../../../enums/platform.enum';
import { useSwitchPlatform } from '../../../hooks/useSwitchPlatform';
import { getUserPlatform } from '../../../utils/utils';

const PLATFORM_OPTIONS: { value: UserPlatformEnum; label: string }[] = [
    { value: UserPlatformEnum.SAAS, label: 'Seguimiento de rutas' },
    { value: UserPlatformEnum.STORK, label: 'Torre de control' },
];

const PlatformSwitcher = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const currentPlatform = getUserPlatform();
    const { mutate, isPending } = useSwitchPlatform();

    const hasBothPlatforms = userInfo?.applications.includes('mindy') && userInfo?.applications.includes('stork');

    if (!hasBothPlatforms) return null;

    const handleSwitch = (target: UserPlatformEnum) => {
        if (target === currentPlatform || isPending) return;

        mutate(target, {
            onError: () => {
                toast({
                    title: t('header.platform.switchError'),
                    description: t('header.platform.switchErrorDescription'),
                    variant: 'destructive',
                });
            },
        });
    };

    const currentPlatformLabel =
        PLATFORM_OPTIONS.find(({ value }) => value === currentPlatform)?.label ?? PLATFORM_OPTIONS[0].label;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    className="h-10 px-3 rounded flex items-center gap-2 bg-gray-100 dark:bg-[#263238] border-0 hover:bg-gray-200 dark:hover:bg-[#37474F] text-gray-900 dark:text-white"
                    disabled={isPending}
                    aria-label={t('header.platform.switcherLabel')}
                >
                    <span className="text-sm font-medium">{currentPlatformLabel}</span>
                    <ChevronDown className="h-4 w-4 opacity-70" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
                {PLATFORM_OPTIONS.map(({ value, label }) => {
                    const isActive = currentPlatform === value;

                    return (
                        <DropdownMenuItem
                            key={value}
                            onSelect={() => handleSwitch(value)}
                            disabled={isPending || isActive}
                            className="cursor-pointer"
                        >
                            <span>{label}</span>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default PlatformSwitcher;
